import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  AttachmentGallery,
  Badge,
  Button,
  Checklist,
  CompactTriggerButton,
  DeskModal,
  DestinationGroup,
  Modal,
  ModalActions,
  Fact,
  NotProvided,
  PersonMark,
  PhotoViewer,
  RelatedGroup,
  SearchSelect,
  RelatedItem,
  SideDrawer,
  StepPath,
  StepTrail,
  SummaryCell,
  SummaryStrip,
  ThumbStrip,
  TransitionPanel,
  kindOfType,
  type ChecklistItem,
  type PathStep,
  type TrailStep,
} from '@evogard/ui';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@evogard/ui/primitives';
import type {
  AddressRecord,
  AnalysisForm,
  Claim,
  ClaimSummary,
  CompensationSummary,
} from '@evogard/contract';
import {
  ANALYSIS_POSSESSION,
  cafStatusLabel,
  claimClosureKindOf,
  formatAge,
  formatInstant,
  type CatalogFormContext,
  type ExpenseRequestStage,
} from '@evogard/domain';
import {
  deskOf,
  destinationLabelOf as deskDestinationLabel,
  formStateOf as stateOf,
  isFieldRequired,
  type Desk,
} from './desk.js';
import { PossessionClose, PossessionRequest } from './PossessionHold.js';
import { CafLinkAction, CafTracking } from './CafLinkAction.js';
import { PhaseForm, fieldAnchor } from './PhaseForm.js';
import {
  ActivityRecords,
  ContactAttempts,
  HoldNotice,
  WithdrawalRequest,
} from './DeskRecords.js';
import { ActivityTab, FilesTab, MessagesTab } from './DeskTabs.js';
import { RepairDrawer, RepairSummaryStrip } from './RepairBlock.js';
import { CompensationDrawer, CompensationSummaryStrip } from './CompensationBlock.js';
import { CompensationDebts, DiscountDrawer } from './CompensationDebts.js';
import { RepairQuotes, type RepairQuotesMode } from './RepairQuotes.js';

/**
 * Onde o orçamento item a item aparece no formulário do Reparo (03/11/2026):
 * monta-se na Orçamentação, compara-se na Aprovação, e a Compra de Peças lê
 * dele as peças da associação.
 */
const REPAIR_QUOTE_MODES: Readonly<Record<string, RepairQuotesMode>> = {
  orcamentacao_oficina: 'editar',
  aprovacao_supervisor: 'aprovar',
  compra_de_pecas: 'pecas',
};
import { UndoMoveNotice, type LastMove } from './UndoMove.js';
import { CLOSURE_KIND_TONE } from '../closures/closureLabels.js';
import { ClosedNotice } from '../closures/ClosedNotice.js';
import { CLAIM_TYPE_OPTIONS, DEPARTMENT_LABEL, labelOf, phaseLabel } from './labels.js';
import { noticeAttachmentItem, useNoticePhotos } from './noticeFiles.js';
import { useClaim } from './useClaims.js';
import { ExpensesTab } from '../expenses/ExpensesTab.js';
import { LaunchExpenseDrawer } from '../expenses/LaunchExpenseDrawer.js';
import { useCardExpenses, useExpenseGrants } from '../expenses/useClaimExpenses.js';
import { ClaimPanel } from './ClaimPanel.js';
import { PersonDrawer } from './PersonDrawer.js';
import { ClaimLabelsHeader } from './ClaimLabels.js';
import { DueDatePicker, PhaseSlaLine } from './ClaimDeadlines.js';
import {
  DeskRefusal,
  useActivity,
  useAssigneeOptions,
  useClaimFiles,
  useClaimMessages,
  useDeskForm,
  useDeskMutations,
  useCompensationSummary,
  useDeskPermissions,
  usePassages,
  useRepairSummary,
} from './useDesk.js';

type MiddleTab = 'formulario' | 'atividade' | 'anexos' | 'mensagens' | 'despesas';

/**
 * A mesa de um departamento — a Análise e o Reparo (07/10/2026): o sinistro
 * aberto por cima da lista, em três colunas — os envolvidos, o miolo
 * (Formulário, Atividade, Anexos) e o movimento.
 *
 * **O departamento é o do sinistro aberto** (`deskOf`, `desk.ts`): abrir pelos
 * envolvidos um aviso que está no Reparo, a partir da Análise, traz a mesa do
 * Reparo. A única diferença entre as duas é o bloco do alto: na Análise, o
 * ocorrido; no Reparo, a oficina, o marco da vez e as peças, com a gaveta do
 * reparo (`RepairBlock.tsx`).
 *
 * O desenho está em `apps/api/src/modules/claims/README.md`, seção "Mover o
 * sinistro de fase". A moldura e as peças são da casa (`DeskModal`,
 * `StepTrail`, `RelatedGroup`, `TransitionPanel`, `Checklist`, `SummaryStrip`,
 * `SideDrawer`) — este arquivo só diz o que vai em cada lugar.
 *
 * - **A trilha é a Análise inteira** (07/10/2026): as cinco fases de
 *   trabalho, na ordem, com a que ele passou marcada (`claims_phase_passages`).
 *   Clicar numa fase por onde ele passou abre o formulário dela PARA CORRIGIR
 *   — o sinistro continua onde está, e o movimento espera a correção acabar.
 * - **O formulário, os destinos e os obrigatórios saem do catálogo**
 *   (`ANALYSIS_PHASES`, `@evogard/domain`) — o mesmo que a API usa para
 *   gravar e para recusar avançar. A lista, o selo e o bloqueio saem de
 *   `missingAnalysisFields`.
 * - **"Salvar e mover" grava o rascunho e move**, numa sequência: se a
 *   gravação falha, não move. Avançar com obrigatório faltando marca os
 *   campos e leva ao primeiro; voltar não pede nada.
 * - **Mover deixa a mesa no mesmo sinistro**, já na fase nova, e mostra por 7
 *   segundos "#214 foi para…" com **Desfazer** dentro dela, porque fora do
 *   modal o aviso não recebe clique (`UndoMove.tsx`). Até 05/10 a mesa abria
 *   o próximo da lista (item 15 do protótipo); o dono trocou em 06/10/2026 —
 *   quem abriu um sinistro para trabalhar nele se perdia. Desfazer volta numa
 *   passagem nova; nada é apagado.
 *
 * Não é `<Page>`: mora dentro de `DepartmentBoardScreen`, que é a tela de rota da Análise e do Reparo.
 */
export function ClaimDeskModal({
  claim,
  onClose,
  onOpenClaim,
  onMoved,
  lastMove,
  onLastMoveDone,
}: {
  /** O sinistro aberto, como a lista o trouxe — `null` fecha o modal. */
  claim: ClaimSummary | null;
  onClose: () => void;
  /** Trocar o modal para outro aviso — a mesa acompanha o departamento dele. */
  onOpenClaim: (claim: ClaimSummary) => void;
  /** O sinistro saiu da fase: a tela da lista recarrega e guarda o Desfazer. */
  onMoved: (move: LastMove) => void;
  /** O movimento anterior, que ainda dá para desfazer — o aviso mora aqui dentro. */
  lastMove: LastMove | null;
  onLastMoveDone: () => void;
}) {
  const [occurrenceOpen, setOccurrenceOpen] = useState(false);
  /** A gaveta do Reparo: a do reparo, ou a do ocorrido aberta a partir dela. */
  const [repairDrawer, setRepairDrawer] = useState<'reparo' | 'ocorrido' | null>(null);
  /** A gaveta da Indenização: a conta, o prazo e os documentos (09/10/2026). */
  const [accountOpen, setAccountOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [tab, setTab] = useState<MiddleTab>('formulario');
  /** O destino escolhido no movimento — é a próxima fase prevista da trilha. */
  const [dest, setDest] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string | null>>({});
  const [marked, setMarked] = useState<ReadonlySet<string>>(new Set());
  /**
   * Sair com rascunho pergunta antes (decisão do dono em 06/10/2026): o X, o
   * Esc, o clique fora e abrir outro sinistro pelos envolvidos descartavam o
   * que foi digitado sem aviso. `next` é para onde se ia — `null` é fechar.
   */
  const [leaving, setLeaving] = useState<Leaving | null>(null);
  /**
   * A fase anterior aberta para corrigir (07/10/2026) — `null` é a atual. O
   * rascunho é sempre da fase que o miolo mostra.
   */
  const [viewing, setViewing] = useState<string | null>(null);
  /**
   * A ficha do aviso, no painel do canto direito por cima da mesa — o mesmo de
   * Sinistros › Sinistros (`ClaimPanel`), para ver e corrigir. Abre pelo botão
   * "Ficha do aviso de sinistro" do alto (08/10/2026); até então o nome do
   * envolvido levava à página da ficha e tirava a pessoa da mesa.
   */
  const [fileId, setFileId] = useState<number | null>(null);
  /**
   * A pessoa de um envolvido — o que o nome dele abre, na gaveta da mesa
   * (`PersonDrawer`). Abrir a gaveta da pessoa fecha a do reparo e a do
   * ocorrido, e o contrário também: a gaveta é uma só.
   */
  const [person, setPerson] = useState<ClaimSummary | null>(null);
  /**
   * O departamento por onde o sinistro já passou, aberto para consulta
   * (07/10/2026) — `null` é a mesa de onde ele está. Do Reparo, o passo
   * "Análise · concluída" dos envolvidos abre a mesa da Análise como ela era
   * vista lá, com a trilha das fases dela; o sinistro não sai do lugar.
   */
  const [lookBack, setLookBack] = useState<Desk['department'] | null>(null);
  const [leaveError, setLeaveError] = useState<string | null>(null);
  /** A gaveta de lançar despesa, aberta pela aba Despesas (26/10/2026). */
  const [expenseDrawer, setExpenseDrawer] = useState(false);
  /** O desconto da Indenização na gaveta: `null` é incluir; ausente, fechada. */
  const [discount, setDiscount] = useState<{ debt: Debt | null } | null>(null);

  /* O aviso aberto é lido inteiro; o do terceiro sobe ao do associado, que é
     de onde vêm os irmãos e o ocorrido. */
  const own = useClaim(claim?.id ?? null);
  const archived = (own.data?.archivedAt ?? claim?.archivedAt ?? null) !== null;
  const parentId = claim?.type === 'terceiro' ? claim.parentClaimId : null;
  const parent = useClaim(parentId);
  const root = parentId ? parent.data : own.data;
  const photos = useNoticePhotos(root?.id ?? 0, root?.noticeAttachments ?? []);
  const loading = own.isLoading || (parentId !== null && parent.isLoading);
  const failed = own.isError || parent.isError;

  /* Onde ele está AGORA: a ficha relida vence a linha da lista. O
     departamento dele escolhe a mesa. */
  const currentPhaseCode = own.data?.currentPhase ?? claim?.currentPhase ?? null;
  const department = own.data?.currentDepartment ?? claim?.currentDepartment ?? null;
  const desk = deskOf(department);
  const current =
    desk && currentPhaseCode ? desk.phaseOf(currentPhaseCode)?.code : undefined;
  const deskMutations = useDeskMutations(desk, claim?.id ?? 0);
  const { canMove } = useDeskPermissions(desk);
  /* O encerrado é só para consulta (22/10/2026): trava tudo, como o
     arquivado, e mudar alguma coisa pede reabrir. */
  const closed =
    desk !== null &&
    current !== undefined &&
    claimClosureKindOf(desk.department, current) !== null;
  const locked = archived || closed;
  const isRepair = desk?.department === 'reparo';
  const isCompensation = desk?.department === 'indenizacao';

  /* A mesa que o miolo mostra: a de onde ele está, ou a de um departamento
     por onde ele já passou (`lookBack`). */
  const past = lookBack !== null && lookBack !== desk?.department;
  const shownDesk = past ? deskOf(lookBack) : desk;

  const id = claim?.id ?? null;
  const passages = usePassages(id);
  /* De onde ele saiu para o desfecho — para dizer, ao reabrir, para onde volta. */
  const closedFrom = closed
    ? ((passages.data ?? []).find(
        (p) => p.department === desk?.department && p.leftAt === null,
      )?.fromPhase ?? null)
    : null;
  const visited = (passages.data ?? [])
    .filter((p) => p.department === shownDesk?.department && p.leftAt !== null)
    .map((p) => p.phase);
  /* No departamento de antes, abre a última etapa de trabalho por onde ele
     passou — a de onde ele saiu. */
  const lastVisited = past
    ? visited.filter((code) => shownDesk?.workStages.includes(code)).at(-1)
    : undefined;
  const pastPhase = past ? (viewing ?? lastVisited ?? null) : null;
  const currentForm = useDeskForm(desk, id, current ?? null);
  const viewingForm = useDeskForm(shownDesk, id, past ? pastPhase : viewing);
  const repair = useRepairSummary(id, isRepair);
  const compensation = useCompensationSummary(id, isCompensation);
  /* A fase que o miolo mostra: a do departamento de antes, a corrigida, ou a atual. */
  const shown = past ? (pastPhase ?? undefined) : (viewing ?? current);
  const activity = useActivity(id);
  const files = useClaimFiles(id);
  /* A aba Despesas é do departamento MOSTRADO — o de agora, ou o consultado —,
     e aparece para quem lança ou supervisiona ali (26/10/2026). */
  const expenseStage = shownDesk?.department ?? null;
  const expenseGrants = useExpenseGrants(expenseStage);
  const expenseTab = expenseGrants.launch || expenseGrants.supervise;
  const cardExpenses = useCardExpenses(expenseStage, id, expenseTab);
  const messages = useClaimMessages(id);

  /* Trocar de sinistro ou de fase começa um rascunho novo. */
  useEffect(() => {
    setDraft({});
    setMarked(new Set());
    setDest(null);
    setViewing(null);
  }, [id, current]);

  /* O que as condições do catálogo perguntam: o tipo do sinistro e o do
     evento — a procuração da Indenização só no furto ou roubo (09/10/2026). */
  const formContext: CatalogFormContext = {
    claimType: claim?.type ?? 'associado',
    claimEvent: own.data?.claimType ?? claim?.claimType ?? null,
  };
  const name = claim ? nameOf(claim) : null;
  const reset = () => {
    setDiscount(null);
    setPerson(null);
    setOccurrenceOpen(false);
    setRepairDrawer(null);
    setAccountOpen(false);
    setPhotoIndex(0);
    setTab('formulario');
    setLookBack(null);
  };

  /** O rascunho que ainda não foi gravado — anexo e registro gravam na hora. */
  const dirty = Object.keys(draft).length > 0;
  /** Trocar a fase que o miolo mostra — o rascunho é dela, e recomeça. */
  const view = (phase: string | null) => {
    setDraft({});
    setMarked(new Set());
    setTab('formulario');
    setViewing(phase);
  };
  const leave = (to: Leaving) => {
    setLeaving(null);
    setLeaveError(null);
    if (to.kind === 'fase') {
      view(to.phase);
      return;
    }
    if (to.kind === 'departamento') {
      view(null);
      setLookBack(to.department);
      return;
    }
    reset();
    if (to.next) {
      onOpenClaim(to.next);
      setLookBack(to.department ?? null);
    } else onClose();
  };
  const askOrLeave = (to: Leaving) => {
    if (dirty) setLeaving(to);
    else leave(to);
  };
  const saveAndLeave = async () => {
    if (!leaving || !shown) return;
    setLeaveError(null);
    try {
      await deskMutations.save.mutateAsync({ phase: shown, values: draft });
      setDraft({});
      leave(leaving);
    } catch (e) {
      setLeaveError(e instanceof Error ? e.message : 'Não foi possível salvar.');
    }
  };

  const openField = (key: string) => {
    setTab('formulario');
    /* O campo só existe depois de a aba desenhar. */
    requestAnimationFrame(() => {
      const el = document.getElementById(fieldAnchor(key));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el?.querySelector<HTMLElement>('input, button, textarea')?.focus({
        preventScroll: true,
      });
    });
  };

  return (
    <>
      <DeskModal
        open={claim !== null}
        onOpenChange={(open) => {
          if (!open) askOrLeave({ kind: 'sinistro', next: null });
        }}
        title={
          claim && (
            <span>
              <span className="tabular-nums text-texto-suave">#{claim.id}</span> ·{' '}
              {name ?? 'nome não informado'}
            </span>
          )
        }
        descricao={
          claim &&
          [
            DEPARTMENT_LABEL[department ?? claim.currentDepartment],
            claim.type === 'terceiro'
              ? `Terceiro do #${claim.parentClaimId}`
              : 'Do associado',
            labelOf(CLAIM_TYPE_OPTIONS, claim.claimType),
            plateOf(claim),
            ageInPhaseOf(passages.data, department),
          ]
            .filter(Boolean)
            .join(' · ')
        }
        /* A ficha, o analista e o vencimento, abaixo do nome (08/10/2026): são
           do card inteiro, e não da fase — a ficha relida vence a linha da
           lista. As etiquetas foram para o alto do miolo, acima das abas: muitas
           etiquetas não empurram estas pastilhas. */
        belowTitle={
          claim && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {/* A ficha do aviso, no painel do canto direito: pastilha só com o
                 ícone, como as do analista e do vencimento ao lado. */}
              <CompactTriggerButton
                compact={{
                  icon: 'documento',
                  tone: 'neutro',
                  title: 'Ficha do aviso de sinistro',
                }}
                onClick={() => setFileId(claim.id)}
              />
              {desk && (
                <>
                  <AssigneePicker
                    desk={desk}
                    claimId={claim.id}
                    assignee={own.data ? own.data.assignee : claim.assignee}
                    enabled={canMove && !locked}
                  />
                  {/* O vencimento do card neste departamento (07/10/2026). */}
                  <DueDatePicker
                    claimId={claim.id}
                    department={own.data?.currentDepartment ?? claim.currentDepartment}
                    dueAt={own.data ? own.data.dueAt : claim.dueAt}
                    enabled={!locked}
                  />
                </>
              )}
            </div>
          )
        }
        summary={
          isCompensation && !past ? (
            <CompensationSummaryStrip
              summary={compensation}
              open={!person && accountOpen}
              onToggle={() => {
                setDiscount(null);
                setPerson(null);
                setAccountOpen((v) => !v || person !== null);
              }}
            />
          ) : isRepair && !past ? (
            <RepairSummaryStrip
              summary={repair}
              phase={current ?? null}
              open={!person && repairDrawer === 'reparo'}
              onToggle={() => {
                setDiscount(null);
                setPerson(null);
                setRepairDrawer((v) => (v === null || person ? 'reparo' : null));
              }}
            />
          ) : (
            root && (
              <OccurrenceSummary
                root={root}
                photos={photos}
                cafStatus={
                  desk?.department === 'analise'
                    ? (own.data?.cafStatus ?? claim?.cafStatus ?? null)
                    : undefined
                }
                open={!person && occurrenceOpen}
                onToggle={() => {
                  setDiscount(null);
                  setPhotoIndex(0);
                  setPerson(null);
                  setOccurrenceOpen((v) => !v || person !== null);
                }}
                onOpenPhoto={(index) => {
                  setDiscount(null);
                  setPhotoIndex(index);
                  setPerson(null);
                  setOccurrenceOpen(true);
                }}
              />
            )
          )
        }
        trail={
          claim && (
            <>
              <Trail
                desk={shownDesk}
                current={past ? undefined : current}
                dest={past ? null : dest}
                visited={visited}
                viewing={past ? pastPhase : viewing}
                onView={(phase) => {
                  if (phase !== (past ? pastPhase : viewing))
                    askOrLeave({ kind: 'fase', phase });
                }}
              />
              {/* O SLA da fase onde o card está, com o tempo usado (07/10/2026). */}
              <PhaseSlaLine
                claim={own.data ?? claim}
                phaseName={phaseLabel(
                  own.data?.currentDepartment ?? claim.currentDepartment,
                  own.data?.currentPhase ?? claim.currentPhase,
                )}
              />
            </>
          )
        }
        leftLabel="Envolvidos no sinistro"
        left={
          claim && (
            <Involved
              claim={claim}
              root={root}
              loading={loading}
              failed={failed}
              shownDepartment={shownDesk?.department ?? null}
              onOpenFile={setFileId}
              onOpenPerson={(p) => {
                setDiscount(null);
                setOccurrenceOpen(false);
                setRepairDrawer(null);
                setAccountOpen(false);
                setPerson(p);
              }}
              onOpenClaim={(other, department) =>
                other.id === claim.id
                  ? askOrLeave({ kind: 'departamento', department: department ?? null })
                  : askOrLeave({ kind: 'sinistro', next: other, department })
              }
            />
          )
        }
        middleLabel="Conteúdo do sinistro"
        right={
          claim &&
          (past && shownDesk && desk ? (
            <LookBackPanel
              shown={shownDesk}
              where={desk}
              current={current}
              onBack={() => askOrLeave({ kind: 'departamento', department: null })}
            />
          ) : (
            <Movement
              desk={desk}
              claimId={claim.id}
              context={formContext}
              current={current}
              dest={dest}
              onDest={setDest}
              archived={archived}
              closedFrom={closedFrom}
              form={currentForm.data}
              draft={viewing ? NO_DRAFT : draft}
              correcting={viewing}
              onMark={(keys) => {
                setMarked(new Set(keys));
                if (keys[0]) openField(keys[0]);
              }}
              onOpenField={openField}
              onSaved={() => setDraft({})}
              onMoved={(move) => {
                reset();
                onMoved(move);
              }}
              notice={
                lastMove && <UndoMoveNotice move={lastMove} onDone={onLastMoveDone} />
              }
            />
          ))
        }
        drawer={
          discount && claim ? (
            <DiscountDrawer
              claimId={claim.id}
              debt={discount.debt}
              onClose={() => setDiscount(null)}
            />
          ) : expenseDrawer && claim && expenseStage ? (
            <LaunchExpenseDrawer
              stage={expenseStage}
              originId={claim.id}
              cardLabel={`Sinistro #${claim.id}`}
              /* No Pagamento de Débito, a despesa da Indenização é um débito (29/10/2026). */
              debt={
                expenseStage === 'indenizacao' &&
                compensation.data?.debtsEditable === true
              }
              onClose={() => setExpenseDrawer(false)}
            />
          ) : person ? (
            <PersonDrawer person={person} onClose={() => setPerson(null)} />
          ) : isCompensation && claim && accountOpen ? (
            <CompensationDrawer
              claim={claim}
              summary={compensation}
              canEdit={canMove && !locked}
              onClose={() => setAccountOpen(false)}
            />
          ) : isRepair && claim && repairDrawer === 'reparo' ? (
            <RepairDrawer
              claim={claim}
              root={root}
              summary={repair}
              onOpenOccurrence={() => {
                setPhotoIndex(0);
                setRepairDrawer('ocorrido');
              }}
              onClose={() => setRepairDrawer(null)}
            />
          ) : (occurrenceOpen || repairDrawer === 'ocorrido') && claim && root ? (
            <OccurrenceDrawer
              claim={claim}
              root={root}
              photos={photos}
              photoIndex={photoIndex}
              onPhotoIndex={setPhotoIndex}
              caf={
                desk?.department === 'analise' && repairDrawer !== 'ocorrido' ? (
                  <CafTracking claimId={claim.id} enabled={canMove && !locked} />
                ) : undefined
              }
              onBack={
                repairDrawer === 'ocorrido' ? () => setRepairDrawer('reparo') : undefined
              }
              onClose={() => {
                setOccurrenceOpen(false);
                setRepairDrawer(null);
              }}
            />
          ) : null
        }
        onCloseDrawer={() => {
          setExpenseDrawer(false);
          setDiscount(null);
          setPerson(null);
          setOccurrenceOpen(false);
          setRepairDrawer(null);
          setAccountOpen(false);
        }}
      >
        {claim && (
          /* As etiquetas do card no alto do miolo, acima das abas (08/10/2026):
             ali a largura é a do formulário, e muitas etiquetas não empurram as
             pastilhas do cabeçalho. */
          <div className="mb-3">
            <ClaimLabelsHeader
              claimId={claim.id}
              labels={own.data ? own.data.labels : claim.labels}
              enabled={!locked}
            />
          </div>
        )}
        {claim && (
          <Middle
            desk={shownDesk}
            claimId={claim.id}
            context={formContext}
            tab={tab}
            onTab={setTab}
            phase={shown ?? null}
            form={past || viewing ? viewingForm : currentForm}
            lookBack={
              past && shownDesk && desk
                ? {
                    shown: shownDesk,
                    where: desk,
                    current,
                    onBack: () => askOrLeave({ kind: 'departamento', department: null }),
                  }
                : null
            }
            correction={
              !past && viewing && current
                ? {
                    phase: viewing,
                    current,
                    readOnly: closed,
                    dirty,
                    saving: deskMutations.save.isPending,
                    error: deskMutations.save.error?.message ?? null,
                    onSave: () =>
                      deskMutations.save.mutate(
                        { phase: viewing, values: draft },
                        { onSuccess: () => setDraft({}) },
                      ),
                    onBack: () => askOrLeave({ kind: 'fase', phase: null }),
                  }
                : null
            }
            draft={draft}
            marked={marked}
            onChange={(key, value) => {
              setDraft((d) => ({ ...d, [key]: value }));
              setMarked((m) => {
                if (!m.has(key)) return m;
                const next = new Set(m);
                next.delete(key);
                return next;
              });
            }}
            onDiscard={(key) =>
              setDraft((d) => {
                const { [key]: _gone, ...rest } = d;
                return rest;
              })
            }
            onFilesAdded={(key) =>
              setMarked((m) => {
                if (!m.has(key)) return m;
                const next = new Set(m);
                next.delete(key);
                return next;
              })
            }
            activity={activity}
            files={files}
            messages={messages}
            expenses={
              expenseTab && expenseStage
                ? {
                    stage: expenseStage,
                    count: cardExpenses.data?.expenses.length,
                    canLaunch: expenseGrants.launch,
                    onLaunch: () => {
                      setPerson(null);
                      setOccurrenceOpen(false);
                      setRepairDrawer(null);
                      setAccountOpen(false);
                      setDiscount(null);
                      setExpenseDrawer(true);
                    },
                  }
                : null
            }
            locked={locked}
            onOpenDiscount={(debt) => {
              setPerson(null);
              setOccurrenceOpen(false);
              setRepairDrawer(null);
              setAccountOpen(false);
              setExpenseDrawer(false);
              setDiscount({ debt });
            }}
            records={
              !past &&
              desk &&
              current && (
                <ActivityRecords
                  desk={desk}
                  claimId={claim.id}
                  phase={current}
                  form={currentForm.data}
                  enabled={canMove && !locked}
                />
              )
            }
          />
        )}
      </DeskModal>
      <ClaimPanel
        claimId={fileId}
        onClose={() => setFileId(null)}
        onOpenClaim={setFileId}
      />
      <Modal
        open={leaving !== null}
        onOpenChange={(open) => {
          if (!open) {
            setLeaving(null);
            setLeaveError(null);
          }
        }}
        title="Há alterações não salvas"
        descricao={`O que foi digitado em ${shown ? (shownDesk?.phaseOf(shown)?.label ?? shown) : 'esta fase'} ainda não foi gravado. Anexos e registros já estão salvos.`}
        size="md"
      >
        <div className="flex flex-col gap-4">
          {leaveError && <Alert tone="erro">{leaveError}</Alert>}
          <ModalActions>
            <Button variant="secundario" onClick={() => setLeaving(null)}>
              Continuar editando
            </Button>
            <Button variant="perigo" onClick={() => leaving && leave(leaving)}>
              Descartar
            </Button>
            <Button
              loading={deskMutations.save.isPending}
              onClick={() => void saveAndLeave()}
            >
              {leaving?.kind === 'fase'
                ? 'Salvar e trocar de fase'
                : leaving?.kind === 'departamento' || leaving?.next
                  ? 'Salvar e abrir'
                  : 'Salvar e fechar'}
            </Button>
          </ModalActions>
        </div>
      </Modal>
    </>
  );
}

/* ------------------------------------------------------------------------- *
 * A trilha
 * ------------------------------------------------------------------------- */

/**
 * O departamento inteiro numa linha — decisão do dono em 07/10/2026, que troca a de
 * 05/10 ("onde está e a próxima, e só isso"): as cinco fases de trabalho na
 * ordem do pipe, com check na que ele passou, o anel na atual e tracejado na
 * que ele ainda não pisou. Um desfecho entra no fim, como onde a Análise parou.
 *
 * **A fase por onde ele passou é botão**: abre o formulário dela para
 * corrigir (`isAnalysisCorrectablePhase`, a mesma regra que a API usa para
 * aceitar a gravação). A atual vira botão enquanto outra está aberta — é o
 * caminho de volta. O destino escolhido no movimento aparece na ponta.
 */
function Trail({
  desk,
  current,
  dest,
  visited,
  viewing,
  onView,
}: {
  desk: Desk | null;
  current: string | undefined;
  dest: string | null;
  /** As fases das passagens do departamento já fechadas. */
  visited: readonly string[];
  viewing: string | null;
  onView: (phase: string | null) => void;
}) {
  if (!desk) return null;
  if (!current) {
    /* Um departamento por onde ele já passou: não há fase atual, e toda etapa
       visitada abre o que foi registrado nela. */
    const steps: TrailStep[] = desk.workStages.map((code) => {
      const done = visited.includes(code);
      return {
        key: code,
        label: desk.phaseOf(code)?.label ?? code,
        state: done ? 'done' : 'next',
        selected: code === viewing,
        onSelect: done && code !== viewing ? () => onView(code) : undefined,
      };
    });
    return (
      <StepTrail
        label={`As fases ${desk.of}, por onde o sinistro passou`}
        steps={steps}
        aside={`Já saiu ${desk.of}`}
      />
    );
  }
  const work = desk.workStages;
  const phases = work.includes(current) ? work : [...work, current];
  const steps: TrailStep[] = phases.map((code) => {
    const now = code === current;
    const correctable = desk.isCorrectable(code, current, visited);
    const label = desk.phaseOf(code)?.label ?? code;
    return {
      key: code,
      /* O desfecho é o último passo, e diz onde o sinistro foi parar. */
      label:
        now && claimClosureKindOf(desk.department, code) ? `Encerrado · ${label}` : label,
      state: now ? 'now' : correctable ? 'done' : 'next',
      selected: code === viewing,
      onSelect: correctable
        ? () => onView(code)
        : now && viewing !== null
          ? () => onView(null)
          : undefined,
    };
  });
  return (
    <StepTrail
      label={`As fases ${desk.of}, e onde o sinistro está`}
      steps={steps}
      aside={dest ? `Destino: ${destinationLabelOf(desk, dest)}` : undefined}
    />
  );
}

/**
 * "há 2 dias nesta fase" — desde a ENTRADA na fase. Trocar a situação dentro
 * dela (F1: "Status do Envio" da Negativa) abre uma passagem nova na mesma
 * fase, e o relógio não recomeça por isso: conta da primeira passagem da
 * sequência que termina na aberta.
 */
function ageInPhaseOf(
  passages:
    | {
        department: string;
        phase: string;
        fromPhase: string | null;
        enteredAt: string;
        leftAt: string | null;
      }[]
    | undefined,
  department: string | null,
): string | null {
  const own = (passages ?? []).filter((p) => p.department === department);
  const openIndex = own.findIndex((p) => p.leftAt === null);
  if (openIndex < 0) return null;
  let start = openIndex;
  while (start > 0 && own[start]!.fromPhase === own[start]!.phase) start--;
  return `${formatAge(Date.now() - new Date(own[start]!.enteredAt).getTime())} nesta fase`;
}

/* ------------------------------------------------------------------------- *
 * O ocorrido
 * ------------------------------------------------------------------------- */

/**
 * O bloco do ocorrido, no cabeçalho (item 5). O local cabe numa linha, e o
 * endereço inteiro fica na dica.
 */
function OccurrenceSummary({
  root,
  photos,
  cafStatus,
  open,
  onToggle,
  onOpenPhoto,
}: {
  root: Claim;
  photos: NoticePhotos;
  /** Na Análise, a situação da CAF (`claims.caf_status`), nula sem resultado. Fora dela, ausente. */
  cafStatus?: string | null;
  open: boolean;
  onToggle: () => void;
  onOpenPhoto: (index: number) => void;
}) {
  const address = root.occurrenceAddress;
  const city = cityOf(address);
  const others = otherFilesOf(root).length;
  return (
    <SummaryStrip
      label={cafStatus === undefined ? 'Ocorrido · do aviso' : 'Ocorrido e CAF'}
      action={{
        label:
          cafStatus === undefined
            ? open
              ? 'Fechar ocorrido'
              : 'Ver ocorrido'
            : open
              ? 'Fechar ocorrido e CAF'
              : 'Ver ocorrido e CAF',
        onClick: onToggle,
        expanded: open,
      }}
      preview={
        photos.isError ? (
          <p className="max-w-[8rem] text-xs leading-snug text-erro">
            As fotos do aviso não carregaram.
          </p>
        ) : photos.isLoading ? (
          <p className="max-w-[8rem] text-xs leading-snug text-texto-tenue">
            Carregando as fotos…
          </p>
        ) : (
          <ThumbStrip
            photos={photos.data ?? []}
            onOpen={onOpenPhoto}
            empty={
              others === 0
                ? 'O aviso chegou sem foto.'
                : `Sem foto — ${others} ${others === 1 ? 'outro arquivo' : 'outros arquivos'} no aviso.`
            }
          />
        )
      }
    >
      <SummaryCell
        value={<span className="tabular-nums">{formatInstant(root.occurredAt)}</span>}
        detail={formatAge(Date.now() - new Date(root.occurredAt).getTime())}
        noShrink
      />
      <SummaryCell
        value={streetOf(address) ?? 'Local não informado'}
        detail={city}
        title={fullAddressOf(address) ?? undefined}
      />
      {cafStatus !== undefined && (
        <SummaryCell
          value={cafStatus ? cafStatusLabel(cafStatus) : 'Sem resultado da CAF'}
          detail="documentação da CAF"
          noShrink
        />
      )}
    </SummaryStrip>
  );
}

/**
 * A gaveta do ocorrido (item 6): os dados do aviso e o relato, ao lado do
 * formulário. O terceiro mostra o ocorrido do aviso do associado (item 7).
 */
function OccurrenceDrawer({
  claim,
  root,
  photos,
  photoIndex,
  onPhotoIndex,
  caf,
  onBack,
  onClose,
}: {
  claim: ClaimSummary;
  root: Claim;
  photos: NoticePhotos;
  photoIndex: number;
  onPhotoIndex: (index: number) => void;
  /**
   * Na Análise, a documentação da CAF, abaixo do ocorrido e separada dele
   * (20/10/2026) — na mesma rolagem, sem aba: ver depressa o que chegou.
   */
  caf?: ReactNode;
  /** Aberta a partir da gaveta do reparo: o caminho de volta a ela. */
  onBack?: () => void;
  onClose: () => void;
}) {
  const others = otherFilesOf(root);
  const driver =
    root.driverIsMember === 'sim'
      ? (root.vehicle?.memberName ?? 'o associado')
      : root.driverName;
  const occurrence = (
    <>
      {onBack && (
        <Button size="sm" variant="discreto" icon="seta-esquerda" onClick={onBack}>
          Voltar ao reparo
        </Button>
      )}
      {claim.id !== root.id && (
        <Alert tone="informacao">Mesmo ocorrido do aviso do associado, #{root.id}.</Alert>
      )}
      {photos.isError && (
        <Alert tone="erro">
          As fotos do aviso não carregaram: {photos.error.message}
        </Alert>
      )}
      {photos.data && photos.data.length > 0 && (
        <PhotoViewer
          photos={photos.data}
          index={photoIndex}
          onIndexChange={onPhotoIndex}
        />
      )}
      {/* Vídeo, áudio e documento: a galeria da casa, que assina no clique e
            abre o visualizador dela. */}
      {others.length > 0 && (
        <AttachmentGallery
          label="Outros arquivos do aviso"
          items={others.map((d) => noticeAttachmentItem(root.id, d))}
        />
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
        <Fact label="Ocorrido em">
          <span className="tabular-nums">{formatInstant(root.occurredAt)}</span>
        </Fact>
        <Fact label="Avisado em">
          <span className="tabular-nums">{formatInstant(root.notifiedAt)}</span>
        </Fact>
        <Fact label="Local" wide>
          {fullAddressOf(root.occurrenceAddress) ?? <NotProvided />}
        </Fact>
        <Fact label="Tipo">{labelOf(CLAIM_TYPE_OPTIONS, root.claimType)}</Fact>
        <Fact label="Condutor">{driver ?? <NotProvided />}</Fact>
      </dl>
      <section aria-label="Relato do associado" className="flex flex-col gap-1.5">
        <h4 className="text-xs font-semibold tracking-wide text-texto-tenue uppercase">
          Relato do associado
        </h4>
        <p className="rounded-borda border border-borda bg-superficie-suave px-3 py-2.5 text-sm leading-relaxed whitespace-pre-line">
          {root.description}
        </p>
      </section>
    </>
  );
  return (
    <SideDrawer
      eyebrow={`Do aviso #${root.id}`}
      title={caf ? 'O ocorrido e a CAF' : 'O ocorrido'}
      closeLabel={caf ? 'Fechar o ocorrido e a CAF' : 'Fechar o ocorrido'}
      onClose={onClose}
    >
      {occurrence}
      {caf && (
        <section
          aria-label="Documentação da CAF"
          className="mt-3 flex flex-col gap-4 border-t border-borda pt-5"
        >
          <h3 className="text-sm font-semibold">Documentação da CAF</h3>
          {caf}
        </section>
      )}
    </SideDrawer>
  );
}

type NoticePhotos = ReturnType<typeof useNoticePhotos>;

/** Os arquivos do aviso que não são foto — vão para a galeria, não para o visualizador. */
function otherFilesOf(root: Claim) {
  return root.noticeAttachments.filter((d) => kindOfType(d.mimeType) !== 'image');
}

/* ------------------------------------------------------------------------- *
 * Os envolvidos
 * ------------------------------------------------------------------------- */

/** O associado e os terceiros do sinistro, numa lista leve. */
function Involved({
  claim,
  root,
  loading,
  failed,
  shownDepartment,
  onOpenClaim,
  onOpenFile,
  onOpenPerson,
}: {
  claim: ClaimSummary;
  root: Claim | undefined;
  loading: boolean;
  failed: boolean;
  /** O departamento cuja mesa o miolo mostra agora. */
  shownDepartment: Desk['department'] | null;
  /** Abrir a mesa de um aviso — num departamento por onde ele já passou, ou `undefined` no de agora. */
  onOpenClaim: (claim: ClaimSummary, department?: Desk['department']) => void;
  /** Abrir a ficha de um aviso no painel por cima da mesa. */
  onOpenFile: (id: number) => void;
  /** Abrir os dados da pessoa de um aviso no painel por cima da mesa. */
  onOpenPerson: (claim: ClaimSummary) => void;
}) {
  const thirds = root?.thirdPartyClaims ?? [];
  return (
    <>
      {loading && (
        <p className="px-2 text-sm text-texto-suave">Carregando os envolvidos…</p>
      )}
      {failed && !loading && (
        <Alert tone="erro">Não foi possível ler os envolvidos deste sinistro.</Alert>
      )}
      {root && (
        <>
          <RelatedGroup title="Associado">
            <Person
              member={root}
              openId={claim.id}
              shownDepartment={shownDepartment}
              onOpenClaim={onOpenClaim}
              onOpenFile={onOpenFile}
              onOpenPerson={onOpenPerson}
            />
          </RelatedGroup>
          <RelatedGroup
            title="Terceiros"
            count={thirds.length}
            empty="Nenhum terceiro neste sinistro."
          >
            {thirds.map((t) => (
              <Person
                key={t.id}
                member={t}
                openId={claim.id}
                shownDepartment={shownDepartment}
                onOpenClaim={onOpenClaim}
                onOpenFile={onOpenFile}
                onOpenPerson={onOpenPerson}
              />
            ))}
          </RelatedGroup>
        </>
      )}
    </>
  );
}

/**
 * Uma pessoa: quem é, o aviso e a placa, e a trilha dos departamentos por
 * onde o aviso dela passou. O nome abre os dados da pessoa na gaveta da
 * mesa (`PersonDrawer`, 08/10/2026), a mesma do reparo, sem sair dela — a ficha do
 * aviso abre pelo botão do alto; o passo de um departamento
 * com mesa (a Análise, o Reparo e a Indenização) abre a mesa dele aqui mesmo — a de onde o
 * aviso está, ou, num departamento por onde ele já passou, a mesma mesa para
 * consulta, com a trilha das fases de lá (07/10/2026). A Indenização tem mesa
 * desde 09/10/2026; o passo de um departamento sem mesa abre a ficha — o
 * rótulo diz qual acontece.
 */
function Person({
  member,
  openId,
  shownDepartment,
  onOpenClaim,
  onOpenFile,
  onOpenPerson,
}: {
  member: ClaimSummary;
  openId: number;
  shownDepartment: Desk['department'] | null;
  onOpenClaim: (claim: ClaimSummary, department?: Desk['department']) => void;
  onOpenFile: (id: number) => void;
  onOpenPerson: (claim: ClaimSummary) => void;
}) {
  const openFile = () => onOpenFile(member.id);
  const self = member.id === openId;
  const plate = plateOf(member);

  /* Todo sinistro nasce na Análise; quem está em outro departamento já passou
     por ela. O Reparo e a Indenização aparecem quando são onde ele está. */
  const departments: {
    department: 'analise' | 'reparo' | 'indenizacao';
    now: boolean;
  }[] = [{ department: 'analise', now: member.currentDepartment === 'analise' }];
  if (member.currentDepartment !== 'analise') {
    departments.push({ department: member.currentDepartment, now: true });
  }

  const steps: PathStep[] = departments.map((step) => {
    const stepDesk = deskOf(step.department);
    const here = self && stepDesk !== null && stepDesk.department === shownDepartment;
    /* Onde ele encerrou, o selo diz "encerrado" na cor do desfecho, e a fase
       vem embaixo: "aberta" ao lado de uma desistência se lia como o sinistro
       aberto, e é só a mesa. */
    const closure = step.now
      ? claimClosureKindOf(step.department, member.currentPhase)
      : null;
    return {
      key: step.department,
      title: DEPARTMENT_LABEL[step.department],
      badge: closure ? (
        <Badge tone={CLOSURE_KIND_TONE[closure]}>encerrado</Badge>
      ) : (
        here && <Badge tone="informacao">{step.now ? 'aberta' : 'consulta'}</Badge>
      ),
      detail: step.now
        ? phaseLabel(step.department, member.currentPhase) || 'sem fase'
        : 'concluída',
      state: step.now ? 'now' : 'done',
      current: here,
      actionLabel: here
        ? `${capitalized(stepDesk.the)} do aviso #${member.id} está aberta`
        : stepDesk
          ? step.now
            ? `Abrir ${stepDesk.the} do aviso #${member.id}`
            : `Ver ${stepDesk.the} do aviso #${member.id}, por onde ele já passou`
          : `Abrir a ficha do aviso #${member.id}`,
      onSelect: () =>
        stepDesk
          ? onOpenClaim(member, step.now ? undefined : stepDesk.department)
          : openFile(),
    };
  });

  return (
    <RelatedItem
      name={nameOf(member)}
      avatarTone={member.type === 'terceiro' ? 'marca' : 'escura'}
      onOpen={() => onOpenPerson(member)}
      openLabel={`Ver os dados do ${member.type === 'terceiro' ? 'terceiro' : 'associado'} do aviso #${member.id}`}
      selected={self}
      subtitle={
        <>
          Aviso <span className="tabular-nums">#{member.id}</span>
          {plate && (
            <>
              {' · '}
              <span className="font-mono tabular-nums">{plate}</span>
            </>
          )}
        </>
      }
    >
      <StepPath label={`Por onde o aviso #${member.id} passou`} steps={steps} />
    </RelatedItem>
  );
}

/* ------------------------------------------------------------------------- *
 * O miolo
 * ------------------------------------------------------------------------- */

type FormQuery = ReturnType<typeof useDeskForm>;

function Middle({
  desk,
  claimId,
  context,
  tab,
  onTab,
  phase,
  form,
  lookBack,
  correction,
  draft,
  marked,
  onChange,
  onDiscard,
  onFilesAdded,
  activity,
  files,
  messages,
  expenses,
  locked,
  onOpenDiscount,
  records,
}: {
  desk: Desk | null;
  claimId: number;
  context: CatalogFormContext;
  tab: MiddleTab;
  onTab: (tab: MiddleTab) => void;
  phase: string | null;
  form: FormQuery;
  /** Um departamento por onde ele já passou, aberto para consulta — `null` no de agora. */
  lookBack: LookBack | null;
  /** A fase anterior aberta para corrigir, com o salvar dela — `null` na atual. */
  correction: Correction | null;
  draft: Record<string, string | null>;
  marked: ReadonlySet<string>;
  onChange: (key: string, value: string | null) => void;
  /** Tira um campo do rascunho — o comentário que acabou de ser gravado. */
  onDiscard: (key: string) => void;
  onFilesAdded: (key: string) => void;
  activity: ReturnType<typeof useActivity>;
  files: ReturnType<typeof useClaimFiles>;
  messages: ReturnType<typeof useClaimMessages>;
  /** A aba Despesas — `null` para quem não lança nem supervisiona no departamento. */
  expenses: {
    stage: ExpenseRequestStage;
    count: number | undefined;
    /** Pode lançar — sem isso, a aba só mostra (quem só supervisiona). */
    canLaunch: boolean;
    onLaunch: () => void;
  } | null;
  /** Arquivado ou encerrado: só consulta, sem comentário novo (22/10/2026). */
  locked: boolean;
  /** Abre a gaveta do desconto da Indenização — `null` para incluir. */
  onOpenDiscount: (debt: Debt | null) => void;
  /** A tentativa de contato e a pendência, no alto da Atividade — nada na consulta. */
  records: ReactNode;
}) {
  const mutations = useDeskMutations(desk, claimId);
  const { canComment } = useDeskPermissions(desk);
  const [uploading, setUploading] = useState<string | null>(null);

  const count = (n: number | undefined) =>
    n === undefined ? null : (
      <span className="ml-1.5 rounded-pilula bg-superficie-suave px-1.5 text-xs tabular-nums text-texto-suave">
        {n}
      </span>
    );

  return (
    <Tabs value={tab} onValueChange={(v) => onTab(v as MiddleTab)}>
      <TabsList>
        <TabsTrigger value="formulario">Formulário</TabsTrigger>
        <TabsTrigger value="atividade">
          Atividade{count(activity.data?.length)}
        </TabsTrigger>
        <TabsTrigger value="anexos">Anexos{count(files.data?.length)}</TabsTrigger>
        <TabsTrigger value="mensagens">
          Mensagens{count(messages.data?.filter((m) => m.body !== null).length)}
        </TabsTrigger>
        {expenses && (
          <TabsTrigger value="despesas">Despesas{count(expenses.count)}</TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="formulario" className="mt-4 flex flex-col gap-3">
        {!desk || !phase ? (
          <Alert tone="informacao">
            O sinistro está num departamento que ainda não tem mesa: o formulário dele não
            abre daqui.
          </Alert>
        ) : form.isLoading ? (
          <p className="text-sm text-texto-suave">Carregando o formulário…</p>
        ) : form.isError ? (
          <Alert tone="erro">{form.error.message}</Alert>
        ) : form.data ? (
          <>
            {lookBack && <LookBackBar lookBack={lookBack} phase={phase} />}
            {correction && <CorrectionBar desk={desk} correction={correction} />}
            {(mutations.attach.isError || mutations.archiveFile.isError) && (
              <Alert tone="erro">
                {(mutations.attach.error ?? mutations.archiveFile.error)?.message}
              </Alert>
            )}
            <PhaseForm
              desk={desk}
              claimId={claimId}
              claimType={context.claimType}
              claimEvent={context.claimEvent}
              phase={phase}
              form={form.data}
              readOnlyNotice={lookBack === null}
              compact
              fieldAction={(key) =>
                key === 'cafLink' &&
                desk.department === 'analise' &&
                phase === 'entrada' ? (
                  <CafLinkAction
                    claimId={claimId}
                    enabled={
                      form.data!.editable && lookBack === null && correction === null
                    }
                  />
                ) : null
              }
              draft={draft}
              state={stateOf(form.data, draft)}
              marked={marked}
              onChange={onChange}
              uploading={uploading}
              onAttach={(key, picked, types) => {
                setUploading(key);
                mutations.attach.mutate(
                  {
                    phase,
                    field: key,
                    files: picked.map((file, i) => ({ file, typeMime: types[i]! })),
                  },
                  {
                    onSuccess: () => onFilesAdded(key),
                    onSettled: () => setUploading(null),
                  },
                );
              }}
              onArchive={(key, documentId) =>
                mutations.archiveFile.mutate({ phase, field: key, documentId })
              }
              onAddComment={async (key, body) => {
                await mutations.save.mutateAsync({ phase, values: { [key]: body } });
                onDiscard(key);
              }}
              top={
                desk.department === 'reparo' &&
                lookBack === null &&
                correction === null &&
                REPAIR_QUOTE_MODES[phase] ? (
                  <RepairQuotes claimId={claimId} mode={REPAIR_QUOTE_MODES[phase]!} />
                ) : desk.department === 'indenizacao' &&
                  phase === 'pagamento_debito' &&
                  lookBack === null &&
                  correction === null ? (
                  <FormDebts
                    claimId={claimId}
                    editable={form.data.editable}
                    onOpenDiscount={onOpenDiscount}
                    onLaunchExpense={
                      expenses?.canLaunch && expenses.stage === 'indenizacao'
                        ? expenses.onLaunch
                        : null
                    }
                  />
                ) : null
              }
              renderContacts={(required, error) => (
                <ContactAttempts
                  desk={desk}
                  claimId={claimId}
                  phase={phase}
                  form={form.data!}
                  required={required}
                  error={error}
                  editable={
                    form.data!.editable && lookBack === null && correction === null
                  }
                />
              )}
            />
          </>
        ) : null}
      </TabsContent>
      <TabsContent value="atividade" className="mt-4 flex flex-col gap-4">
        {records}
        <ActivityTab
          claimId={claimId}
          items={activity.data}
          loading={activity.isLoading}
          error={activity.error}
          canComment={canComment && !locked}
        />
      </TabsContent>
      <TabsContent value="anexos" className="mt-4">
        <FilesTab
          claimId={claimId}
          files={files.data}
          loading={files.isLoading}
          error={files.error}
        />
      </TabsContent>
      <TabsContent value="mensagens" className="mt-4">
        <MessagesTab
          department={desk?.department ?? null}
          messages={messages.data}
          loading={messages.isLoading}
          error={messages.error}
        />
      </TabsContent>
      {expenses && (
        <TabsContent value="despesas" className="mt-4">
          <ExpensesTab
            stage={expenses.stage}
            originId={claimId}
            onLaunch={expenses.onLaunch}
          />
        </TabsContent>
      )}
    </Tabs>
  );
}

/**
 * Os débitos no formulário do Pagamento de Débito (29/10/2026): o "Lançar
 * despesa" é o MESMO botão da aba Despesas — abre a mesma gaveta, que ali já
 * pede o tipo do débito —, e o "Incluir desconto" é o que só desconta. O modal
 * da conta só mostra.
 */
function FormDebts({
  claimId,
  editable,
  onLaunchExpense,
  onOpenDiscount,
}: {
  claimId: number;
  editable: boolean;
  onLaunchExpense: (() => void) | null;
  onOpenDiscount: (debt: Debt | null) => void;
}) {
  const summary = useCompensationSummary(claimId, true);
  if (summary.isError) return <Alert tone="erro">{summary.error.message}</Alert>;
  if (!summary.data)
    return <p className="text-sm text-texto-suave">Carregando os débitos…</p>;
  return (
    <CompensationDebts
      claimId={claimId}
      summary={summary.data}
      actions={{ editable, onLaunchExpense, onOpenDiscount }}
    />
  );
}

/** Um débito do bloco da Indenização. */
type Debt = CompensationSummary['deductions'][number];

interface LookBack {
  /** A mesa mostrada — a de um departamento por onde ele já passou. */
  shown: Desk;
  /** A mesa de onde ele está. */
  where: Desk;
  current: string | undefined;
  onBack: () => void;
}

/** "Reparo · Entrada" — onde o sinistro está agora. */
function placeOf(l: LookBack): string {
  const phase = l.current ? l.where.phaseOf(l.current)?.label : undefined;
  return [DEPARTMENT_LABEL[l.where.department], phase].filter(Boolean).join(' · ');
}

/**
 * A faixa de quem consulta um departamento por onde o sinistro já passou: diz
 * que ele está em outro lugar, que o que aparece é o que foi registrado lá, e
 * leva de volta à mesa de agora. Corrigir é só onde ele está — a API recusa
 * gravar numa fase de um departamento que ele deixou, e o formulário vem
 * somente para leitura.
 */
function LookBackBar({ lookBack: l, phase }: { lookBack: LookBack; phase: string }) {
  return (
    <Alert
      tone="informacao"
      title={`Consultando ${l.shown.phaseOf(phase)?.label ?? phase}, ${l.shown.of}`}
    >
      <p>
        O sinistro já saiu {l.shown.of} e está em {placeOf(l)}. Aqui está, só para
        leitura, o que foi registrado nesta fase; as outras abrem pela trilha no alto.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button size="sm" variant="secundario" onClick={l.onBack}>
          Voltar para {l.where.the}
        </Button>
      </div>
    </Alert>
  );
}

/** O lado do movimento enquanto se consulta um departamento de antes: dali ele não se move. */
function LookBackPanel(l: LookBack) {
  return (
    <TransitionPanel
      action={
        <Button variant="secundario" onClick={l.onBack}>
          Voltar para {l.where.the}
        </Button>
      }
    >
      <p className="text-sm text-texto-suave">
        Você está consultando {l.shown.the}, por onde o sinistro já passou. Ele está em{' '}
        {placeOf(l)}, e se move de lá.
      </p>
    </TransitionPanel>
  );
}

interface Correction {
  phase: string;
  current: string;
  /** O sinistro está encerrado: a fase de antes abre só para consulta (22/10/2026). */
  readOnly: boolean;
  dirty: boolean;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onBack: () => void;
}

/**
 * A faixa de quem está corrigindo uma fase anterior: diz que o sinistro não
 * saiu do lugar, grava a correção e leva de volta à fase atual. Anexo sobe na
 * hora, como na atual; o rascunho é o que este "Salvar" grava.
 */
function CorrectionBar({ desk, correction: c }: { desk: Desk; correction: Correction }) {
  const label = (code: string) => desk.phaseOf(code)?.label ?? code;
  if (c.readOnly) {
    return (
      <Alert tone="informacao" title={`Consultando ${label(c.phase)}`}>
        <p>
          O sinistro está encerrado em {label(c.current)}. Aqui está, só para leitura, o
          que foi registrado nesta fase; as outras abrem pela trilha no alto.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" variant="secundario" onClick={c.onBack}>
            Voltar para {label(c.current)}
          </Button>
        </div>
      </Alert>
    );
  }
  return (
    <Alert tone="informacao" title={`Corrigindo ${label(c.phase)}`}>
      <p>
        O sinistro continua em {label(c.current)}. O que você salvar aqui corrige o que
        foi registrado nesta fase, e fica na Atividade.
      </p>
      {c.error && <p className="mt-1 text-erro">{c.error}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <Button
          size="sm"
          disabled={!c.dirty || c.saving}
          loading={c.saving}
          onClick={c.onSave}
        >
          Salvar correção
        </Button>
        <Button size="sm" variant="secundario" onClick={c.onBack}>
          Voltar para {label(c.current)}
        </Button>
      </div>
    </Alert>
  );
}

/* ------------------------------------------------------------------------- *
 * O movimento
 * ------------------------------------------------------------------------- */

function Movement({
  desk,
  claimId,
  context,
  current,
  dest,
  onDest,
  archived,
  closedFrom,
  form,
  draft,
  correcting,
  onMark,
  onOpenField,
  onSaved,
  onMoved,
  notice,
}: {
  desk: Desk | null;
  claimId: number;
  /** O tipo do sinistro e o do evento — o que as condições do catálogo perguntam. */
  context: CatalogFormContext;
  current: string | undefined;
  /** O destino escolhido. Mora na mesa, porque a trilha o mostra. */
  dest: string | null;
  onDest: (dest: string | null) => void;
  archived: boolean;
  /** No encerrado, a fase de onde ele saiu para o desfecho — o reabrir diz para onde volta. */
  closedFrom: string | null;
  form: AnalysisForm | undefined;
  draft: Record<string, string | null>;
  /**
   * A fase anterior aberta no miolo. Enquanto ela está aberta o sinistro não
   * se move: o "Salvar e mover" gravaria o rascunho da fase errada.
   */
  correcting: string | null;
  onMark: (keys: string[]) => void;
  onOpenField: (key: string) => void;
  onSaved: () => void;
  onMoved: (move: LastMove) => void;
  /** O Desfazer do movimento anterior, quando a mesa abriu o próximo. */
  notice: ReactNode;
}) {
  const [refusal, setRefusal] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const mutations = useDeskMutations(desk, claimId);
  const { canMove: mayMove, canSupervise } = useDeskPermissions(desk);

  if (!desk || !current) {
    return (
      <TransitionPanel action={null}>
        {notice}
        <p className="text-sm text-texto-suave">
          O sinistro está num departamento que ainda não tem mesa. Enquanto o Desfazer
          valer, ele volta daqui.
        </p>
      </TransitionPanel>
    );
  }

  if (claimClosureKindOf(desk.department, current)) {
    return (
      <TransitionPanel action={null}>
        {notice}
        <ClosedNotice
          what="o sinistro"
          target={{
            source: 'sinistro',
            id: claimId,
            origin: desk.department,
            phase: current,
            fromPhase: closedFrom,
          }}
        />
      </TransitionPanel>
    );
  }

  /* As etapas do supervisor são dele (07/10/2026; 29/10/2026): sem a
     permissão, o sinistro aparece e não se move. */
  const inSupervision = desk.supervision?.phases.includes(current) ?? false;
  const canMove = mayMove && (!inSupervision || canSupervise);
  const hold = form?.hold?.type ?? null;
  const state = stateOf(form, draft);
  const { forward, handoffs, outcomes, back } = desk.movesOf(
    current,
    hold,
    context,
    state,
  );
  /* O negado com posse (16/10/2026): parado nele, encerrar é o modal dele. */
  const inPossession = hold === ANALYSIS_POSSESSION.hold;
  const destinationLabel = (code: string) => destinationLabelOf(desk, code);
  const toDestinations = (codes: string[]) =>
    codes.map((code) => ({ id: code, name: destinationLabel(code) }));
  /* Confirmar o pedido de desistência não pede o formulário da etapa: o
     cliente desistiu dela inteira (a API decide igual). */
  const confirmsWithdrawal = hold === 'pre_desistencia' && dest === 'desistencia';
  /* Pede o formulário? Avançar pede; voltar e o desvio (o Acordo da
     Indenização, I5) não — `needsForm`, a mesma pergunta que a API faz. */
  const isForward = dest !== null && !confirmsWithdrawal && desk.needsForm(current, dest);
  const isBack = dest !== null && back.includes(dest);
  const sending = dest !== null && desk.handoffOf(dest) !== undefined;
  const outcomesTitle =
    confirmsWithdrawal || hold === 'pre_desistencia'
      ? 'Confirmar o pedido'
      : `Encerrar ${desk.the}`;

  const required = desk
    .shownFields(current, state, context)
    .filter((f) => isFieldRequired(f, state, context));
  const missing = desk.missingFields(current, state, context);
  const missingLabels = new Set(missing.map((f) => f.label));
  const items: ChecklistItem[] = required.map((f) => ({
    key: f.key ?? f.label,
    label: f.label,
    status: missingLabels.has(f.label) ? 'pending' : 'done',
    detail: f.binding.subject === 'unmapped' ? 'ainda não grava' : undefined,
    onSelect: f.key ? () => onOpenField(f.key!) : undefined,
  }));
  const dirty = Object.keys(draft).length > 0;
  const busy = mutations.save.isPending || mutations.move.isPending;

  const summary = !dest
    ? null
    : !isForward
      ? confirmsWithdrawal
        ? 'A desistência não exige o formulário da etapa'
        : isBack
          ? 'Voltar não exige o formulário desta fase'
          : 'Este destino não exige o formulário desta fase'
      : missing.length === 0
        ? 'Pronto para avançar'
        : `${missing.length} ${missing.length === 1 ? 'obrigatório pendente' : 'obrigatórios pendentes'}`;

  const go = async () => {
    if (!dest) return;
    setRefusal(null);
    if (isForward && missing.length > 0) {
      setTried(true);
      onMark(missing.map((f) => f.key).filter((k): k is string => Boolean(k)));
      return;
    }
    try {
      if (dirty) {
        await mutations.save.mutateAsync({ phase: current, values: draft });
        onSaved();
      }
      const moved = await mutations.move.mutateAsync({
        fromPhase: current,
        toPhase: dest,
      });
      onMoved({
        claimId,
        department: desk.department,
        toLabel: destinationLabel(dest),
        passageId: moved.passage.id,
        closed: claimClosureKindOf(desk.department, dest) !== null,
      });
    } catch (e) {
      const r = e instanceof DeskRefusal ? e : null;
      if (r?.code === 'obrigatorios_pendentes') setTried(true);
      setRefusal(e instanceof Error ? e.message : 'Não foi possível mover o sinistro.');
    }
  };

  return (
    <TransitionPanel
      status={
        <>
          {refusal && <Alert tone="erro">{refusal}</Alert>}
          {correcting && (
            <p className="text-xs text-texto-suave">
              Você está corrigindo {desk.phaseOf(correcting)?.label}. Volte para{' '}
              {desk.phaseOf(current)?.label} para mover o sinistro.
            </p>
          )}
          {!canMove && (
            <p className="text-xs text-texto-suave">
              {inSupervision && mayMove
                ? 'A Aprovação Supervisor é do supervisor do reparo: só ele decide.'
                : 'Você vê o sinistro, mas mover e preencher pedem outra permissão.'}
            </p>
          )}
          {archived && (
            <p className="text-xs text-texto-suave">Sinistro arquivado não se move.</p>
          )}
          {summary && (
            <Badge
              tone={
                !isForward || missing.length === 0 ? 'sucesso' : tried ? 'erro' : 'alerta'
              }
            >
              {summary}
            </Badge>
          )}
          {dirty && canMove && !archived && (
            <Button
              variant="secundario"
              loading={mutations.save.isPending}
              disabled={busy}
              onClick={() =>
                mutations.save.mutate(
                  { phase: current, values: draft },
                  {
                    onSuccess: onSaved,
                    onError: (e) => setRefusal(e.message),
                  },
                )
              }
            >
              Salvar sem mover
            </Button>
          )}
        </>
      }
      action={
        <Button
          disabled={!dest || !canMove || archived || busy || correcting !== null}
          loading={busy}
          onClick={() => void go()}
        >
          {dest
            ? `${dirty ? (sending ? 'Salvar e enviar' : 'Salvar e mover') : sending ? 'Enviar' : 'Mover'} para ${destinationLabel(dest)}`
            : 'Escolha um destino'}
        </Button>
      }
    >
      {notice}
      <HoldNotice
        desk={desk}
        claimId={claimId}
        phase={current}
        form={form}
        enabled={canMove && !archived}
      />
      {/* A tentativa de contato e a pendência foram para a aba Atividade
          (08/10/2026); o pedido de desistência fica aqui, como destino. */}
      <WithdrawalRequest
        desk={desk}
        claimId={claimId}
        phase={current}
        form={form}
        enabled={canMove && !archived}
      />
      <PossessionRequest
        desk={desk}
        claimId={claimId}
        phase={current}
        form={form}
        state={state}
        draft={draft}
        onSaved={onSaved}
        enabled={canMove && !archived && correcting === null}
      />
      {inPossession && (
        <PossessionClose
          desk={desk}
          claimId={claimId}
          context={context}
          phase={current}
          phaseDraft={draft}
          onSaved={onSaved}
          onMoved={onMoved}
          enabled={canMove && !archived && correcting === null}
        />
      )}
      <DestinationGroup
        title={inSupervision ? 'Aprovar e enviar para' : 'Avançar para'}
        direction="forward"
        destinations={toDestinations(forward)}
        selected={dest}
        onSelect={(next) => {
          setTried(false);
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      <DestinationGroup
        title="Enviar para outro departamento"
        direction="forward"
        destinations={toDestinations(handoffs)}
        selected={dest}
        onSelect={(next) => {
          setTried(false);
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      <DestinationGroup
        title={outcomesTitle}
        direction="forward"
        destinations={toDestinations(inPossession ? [] : outcomes)}
        selected={dest}
        onSelect={(next) => {
          setTried(false);
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      <DestinationGroup
        title="Voltar para"
        direction="back"
        destinations={toDestinations(back)}
        selected={dest}
        onSelect={(next) => {
          setTried(false);
          setRefusal(null);
          onDest(dest === next ? null : next);
        }}
      />
      {hold && forward.length + handoffs.length + outcomes.length + back.length === 0 && (
        <p className="text-sm text-texto-suave">
          Parado, o sinistro não se move. Retome-o para avançar, encerrar ou voltar.
        </p>
      )}
      {desk.isGeneralPhase(current) && (
        <p className="text-sm text-texto-suave">
          {desk.phaseOf(current)?.label} é uma fase geral, fora {desk.of}, e o envio a
          partir dela ainda não existe.
        </p>
      )}
      <Checklist
        title="Obrigatórios para avançar"
        items={items}
        alert={isForward && (tried || missing.length > 0)}
        empty="Esta fase não tem obrigatório."
      />
    </TransitionPanel>
  );
}

/**
 * O responsável do departamento (07/10/2026) — o Analista Responsável na
 * Análise, o responsável do reparo no Reparo: quem cuida do card enquanto ele
 * está no departamento, atravessando as fases (`claims_assignments`). Mora
 * abaixo do nome na mesa, entre a ficha e o vencimento: o ícone de pessoa e o
 * nome, cortado com reticências se for longo (inteiro no `title`), para não
 * quebrar o cabeçalho. Sem ninguém, a pastilha de pessoa tracejada abre a lista
 * das pessoas. Escolher grava na hora; "Tirar" fica no pé da lista. Quem não
 * pode mover vê a pastilha e não abre nada.
 */
function AssigneePicker({
  desk,
  claimId,
  assignee,
  enabled,
}: {
  desk: Desk;
  claimId: number;
  assignee: ClaimSummary['assignee'];
  enabled: boolean;
}) {
  const options = useAssigneeOptions(desk, enabled);
  const { setAssignee } = useDeskMutations(desk, claimId);
  const title = assignee
    ? `${desk.assigneeLabel}: ${assignee.name}`
    : enabled
      ? `Escolher o ${desk.assigneeLabel.toLowerCase()}`
      : desk.noAssignee;
  if (!enabled) {
    /* Quem não pode escolher vê o mesmo desenho, sem abrir nada. */
    return (
      <PersonMark
        name={assignee?.name ?? null}
        emptyLabel={desk.noAssignee}
        className="max-w-56"
      />
    );
  }
  /* Quem já é responsável e deixou de estar ativo continua aparecendo. */
  const list = options.data ?? [];
  const choices =
    assignee && !list.some((p) => p.id === assignee.id)
      ? [{ id: assignee.id, name: assignee.name }, ...list]
      : list;
  return (
    <div className="flex flex-col items-end gap-1">
      <SearchSelect
        label={desk.assigneeLabel}
        options={choices.map((p) => ({ value: p.id, label: p.name }))}
        value={assignee?.id ?? ''}
        onChange={(v) => setAssignee.mutate(v === '' ? null : v)}
        searchPlaceholder="Buscar pessoa"
        emptyMessage="Ninguém com esse nome nesta base."
        loading={options.isLoading}
        clearable
        sortable
        compact={{
          icon: 'pessoa',
          filled: assignee !== null,
          /* Escolhido, o nome vem escrito ao lado; cortado se for longo. */
          text: assignee?.name,
          title,
          loading: setAssignee.isPending,
        }}
      />
      {(options.isError || setAssignee.isError) && (
        <p role="alert" className="max-w-48 text-right text-xs text-erro">
          {(options.error ?? setAssignee.error)?.message}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Ajudantes
 * ------------------------------------------------------------------------- */

/**
 * Para onde a mesa vai: trocar de sinistro (ou fechar), trocar a fase
 * mostrada, ou trocar o departamento mostrado — `null` volta à mesa de onde
 * ele está. O sinistro de outro envolvido pode abrir já num departamento de antes.
 */
type Leaving =
  | {
      kind: 'sinistro';
      next: ClaimSummary | null;
      department?: Desk['department'] | null;
    }
  | { kind: 'fase'; phase: string | null }
  | { kind: 'departamento'; department: Desk['department'] | null };

const NO_DRAFT: Record<string, string | null> = {};

/**
 * O envio leva o nome do departamento, e não o da antiga fase do pipe
 * ("Aprovado para Reparo" é o Reparo) — 07/10/2026.
 */
function destinationLabelOf(desk: Desk, code: string): string {
  return deskDestinationLabel(desk, code, DEPARTMENT_LABEL);
}

/** "a Análise" → "A Análise". */
function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function nameOf(c: ClaimSummary): string | null {
  return (c.type === 'terceiro' ? c.thirdParty?.name : c.vehicle?.memberName) ?? null;
}

function plateOf(c: ClaimSummary): string | null {
  return (c.type === 'terceiro' ? c.thirdParty?.plate : c.vehicle?.plate) ?? null;
}

/** Logradouro e número — a linha do bloco. */
function streetOf(a: AddressRecord | null): string | null {
  const line = [a?.street, a?.number].filter(Boolean).join(', ');
  return line || null;
}

function cityOf(a: AddressRecord | null): string | null {
  if (!a?.city) return null;
  return [a.neighborhood, `${a.city}${a.state ? `/${a.state}` : ''}`]
    .filter(Boolean)
    .join(' · ');
}

/** O endereço inteiro — a dica do bloco e o "Local" da gaveta. */
function fullAddressOf(a: AddressRecord | null): string | null {
  const line = [streetOf(a), a?.complement, cityOf(a)].filter(Boolean).join(' · ');
  return line || null;
}
